import { sendLocal } from "@sinch/bus";
import "@nectary/components/alert";
import "@nectary/components/button";
import "@nectary/components/icon";
import { createSubscriber } from "@sinch/bus";
import { useEffect, useReducer, type FC } from "react";
import z from "zod";

const TopBannerOpenRequestPayloadSchema = z.object({
  id: z.string().optional(),
  type: z.enum(["info", "warn", "error"]),
  text: z.string(),
  isClosable: z.boolean(),
  isActionAvailable: z.boolean(),
  actionText: z.string(),
  actionHref: z.string(),
});

const TopBannerCloseRequestPayloadSchema = z.object({
  id: z.string().optional(),
});

type TopBannerOpenRequestPayload = z.infer<
  typeof TopBannerOpenRequestPayloadSchema
>;

type TopBannerClickEventPayload = { id: string | undefined };

type AlertState =
  | { visible: true; data: TopBannerOpenRequestPayload }
  | { visible: false };

type AlertAction =
  | { type: "OPEN"; payload: TopBannerOpenRequestPayload }
  | { type: "CLOSE" };

const reducer = (state: AlertState, action: AlertAction): AlertState => {
  switch (action.type) {
    case "OPEN":
      return { visible: true, data: action.payload };
    case "CLOSE":
      return { visible: false };
    default:
      return state;
  }
};

const TOPBANNER_CLOSE_CLICK_EVENT_TYPE = "TopBannerCloseClick";
const TOPBANNER_ACTION_CLICK_EVENT_TYPE = "TopBannerActionClick";
const TOPBANNER_CLOSE_REQUEST_EVENT_TYPE = "TopBannerCloseRequest";
const TOPBANNER_OPEN_REQUEST_EVENT_TYPE = "TopBannerOpenRequest";

export const TopBanner: FC = () => {
  const [state, dispatch] = useReducer(reducer, {
    visible: false,
  });

  useEffect(() => {
    const unsubOpen = createSubscriber(
      TOPBANNER_OPEN_REQUEST_EVENT_TYPE,
      async (msg: unknown) => {
        const result = TopBannerOpenRequestPayloadSchema.safeParse(msg);
        if (!result.success) return;

        dispatch({ type: "OPEN", payload: result.data });
      }
    );

    const unsubClose = createSubscriber(
      TOPBANNER_CLOSE_REQUEST_EVENT_TYPE,
      async (msg: unknown) => {
        const result = TopBannerCloseRequestPayloadSchema.safeParse(msg);
        if (!result.success) return;
        if (
          state.visible &&
          typeof result.data.id !== "undefined" &&
          result.data.id !== state.data?.id
        ) {
          return;
        }
        dispatch({ type: "CLOSE" });
      }
    );

    return () => {
      unsubOpen();
      unsubClose();
    };
  }, []);

  const handleActionClick = () => {
    if (!state.visible) return;
    sendLocal(
      {
        type: TOPBANNER_ACTION_CLICK_EVENT_TYPE,
        payload: {
          id: state.visible ? state.data?.id : undefined,
        } as TopBannerClickEventPayload,
      },
      { isReplayable: true }
    );
    const { actionHref } = state.data;
    try {
      const url = new URL(actionHref, location.href);
      if (url.hostname === location.hostname) {
        return window.history.pushState({}, "", url.href);
      } else {
        window.open(url, "_blank");
      }
    } catch (err: unknown) {
      console.error("Failed to open link in Topbanner", actionHref, err);
    }
  };

  const handleCloseClick = () => {
    sendLocal(
      {
        type: TOPBANNER_CLOSE_CLICK_EVENT_TYPE,
        payload: {
          id: state.visible ? state.data?.id : undefined,
        } as TopBannerClickEventPayload,
      },
      { isReplayable: true }
    );

    dispatch({ type: "CLOSE" });
  };

  if (!state.visible) return null;

  const { type, text, isClosable, isActionAvailable, actionText } = state.data;

  return (
    <sinch-alert type={type} text={text}>
      {isActionAvailable && (
        <sinch-button
          slot="action"
          type="cta-secondary"
          text={actionText}
          aria-label="Action button"
          size="s"
          on-click={() => handleActionClick()}
        />
      )}

      {isClosable && (
        <sinch-button
          slot="close"
          aria-label="Close alert"
          size="s"
          on-click={() => handleCloseClick()}
        >
          <sinch-icon icons-version="2" name="fa-xmark" slot="icon" />
        </sinch-button>
      )}
    </sinch-alert>
  );
};
